import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Layout } from "@/components/layout";
import { useProducts } from "@/hooks/use-products";
import { useToast } from "@/hooks/use-toast";
import { Product } from "@shared/operations-schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, ArrowLeft, Save } from "lucide-react";

// Define form schema for validation
const ProductSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  type: z.enum(["product", "service"]),
  sku: z.string().optional(),
  price: z.number().min(0, "Price must be a positive number"),
  cost: z.number().min(0, "Cost must be a positive number").optional(),
  taxRate: z.number().min(0, "Tax rate must be a positive number").optional(),
  isActive: z.boolean().default(true),
  imageUrl: z.string().optional(),
  unit: z.string().default("each"),
});

// Define type for form values
type ProductFormValues = z.infer<typeof ProductSchema>;

// Default values for the form
const defaultValues: ProductFormValues = {
  name: "",
  description: "",
  type: "product",
  sku: "",
  price: 0,
  cost: 0,
  taxRate: 0,
  isActive: true,
  imageUrl: "",
  unit: "each"
};

export default function ProductEditPage() {
  const { id } = useParams<{ id: string }>();
  const productId = parseInt(id);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<Product | null>(null);
  
  const { 
    products = [], 
    isLoading, 
    updateProductMutation, 
  } = useProducts();

  // Setup form with validation
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(ProductSchema),
    defaultValues,
  });

  // Load product data when component mounts - run only once when products load
  useEffect(() => {
    // Skip if already loaded product matches the requested ID
    if (product && product.id === productId) {
      return;
    }
    
    // First try to get from localStorage (for immediate access)
    const storedProduct = localStorage.getItem('editProduct');
    if (storedProduct) {
      try {
        const parsedProduct = JSON.parse(storedProduct);
        if (parsedProduct.id === productId) {
          console.log("Loading product from localStorage:", parsedProduct);
          setProduct(parsedProduct);
          
          // Set form values
          form.reset({
            name: parsedProduct.name,
            description: parsedProduct.description || "",
            type: parsedProduct.type as "product" | "service",
            sku: parsedProduct.sku || "",
            price: Number(parsedProduct.price),
            cost: parsedProduct.cost ? Number(parsedProduct.cost) : 0,
            taxRate: Number(parsedProduct.taxRate),
            isActive: parsedProduct.isActive === true, // Ensure boolean
            imageUrl: parsedProduct.imageUrl || "",
            unit: parsedProduct.unit || "each",
          });
          
          setLoading(false);
          return; // Exit early if loaded from localStorage
        }
      } catch (error) {
        console.error("Error parsing product from localStorage:", error);
      }
    }
    
    // If not found in localStorage, try to find the product in the products array
    if (products && products.length > 0) {
      const foundProduct = products.find(p => p.id === productId);
      if (foundProduct) {
        console.log("Loading product from API data:", foundProduct);
        setProduct(foundProduct);
        
        // Set form values
        form.reset({
          name: foundProduct.name,
          description: foundProduct.description || "",
          type: foundProduct.type as "product" | "service",
          sku: foundProduct.sku || "",
          price: Number(foundProduct.price),
          cost: foundProduct.cost ? Number(foundProduct.cost) : 0,
          taxRate: Number(foundProduct.taxRate),
          isActive: foundProduct.isActive === true, // Ensure boolean
          imageUrl: foundProduct.imageUrl || "",
          unit: foundProduct.unit || "each",
        });
        
        setLoading(false);
      }
    }
  }, [productId, products, form]);

  // Handle form submission
  const onSubmit = (data: ProductFormValues) => {
    if (!productId) {
      toast({
        title: "Error",
        description: "Product ID is missing",
        variant: "destructive",
      });
      return;
    }
    
    // Log the form data to debug the isActive status
    console.log("Submitting form data:", data);
    
    // Ensure isActive is explicitly included as a boolean
    const updatedData = {
      ...data,
      // Convert price, cost, and taxRate to strings for the API
      price: data.price.toString(),
      cost: data.cost !== undefined ? data.cost.toString() : null,
      taxRate: data.taxRate !== undefined ? data.taxRate.toString() : null,
      isActive: data.isActive
    };
    
    console.log("Processed update data:", updatedData);
    
    updateProductMutation.mutate({ id: productId, data: updatedData }, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Product updated successfully",
        });
        navigate("/sales/products");
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  // If loading or product not found
  if (loading || isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <h1 className="text-xl font-semibold">Product not found</h1>
          <Link href="/sales/products">
            <Button>Go back to products</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-5xl py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Link href="/sales/products">
              <Button variant="outline" size="sm" className="mr-4">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Products
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Edit {product.type === "product" ? "Product" : "Service"}</h1>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Edit {product.name}</CardTitle>
            <CardDescription>
              Update the details of your {product.type === "product" ? "product" : "service"}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>Type</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-col space-y-1"
                          >
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="product" id="product" checked={field.value === "product"} />
                              </FormControl>
                              <FormLabel htmlFor="product" className="font-normal cursor-pointer">
                                Product
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="service" id="service" checked={field.value === "service"} />
                              </FormControl>
                              <FormLabel htmlFor="service" className="font-normal cursor-pointer">
                                Service
                              </FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SKU</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter SKU (optional)" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit</FormLabel>
                        <FormControl>
                          <Input placeholder="each, hour, etc." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cost ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="taxRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax Rate (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image URL</FormLabel>
                        <FormControl>
                          <Input placeholder="Image URL (optional)" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter description"
                          className="resize-none min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Active</FormLabel>
                        <FormDescription>
                          Inactive products will not appear in invoices or estimates.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                
                <div className="flex justify-end space-x-4">
                  <Link href="/sales/products">
                    <Button variant="outline" type="button">Cancel</Button>
                  </Link>
                  <Button 
                    type="submit" 
                    disabled={updateProductMutation.isPending}
                    className="gap-2"
                  >
                    {updateProductMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Save className="h-4 w-4 mr-1" /> Save Changes
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
